/**
 * V2ChatPage — dark, dense V2 restyle of the classic ChatPage.
 *
 * Behaviour is copied verbatim from `web/src/pages/ChatPage.tsx`:
 *   - Messages live in local `useState<ChatMessage[]>` (no store persistence).
 *   - Sending calls `BackendAPI.sendMessageToChatbot(formData)` and AWAITS the
 *     `{ answer, scope_generated, scope_data }` response — the classic chat is
 *     request/response, NOT streaming (no SSE). Replicated exactly.
 *   - Project context: `useParams().projectId` rehydrates the project via
 *     `useProjectStore().getProjectById`; on send we append `project_id` and
 *     `project_address` to the FormData exactly as classic does.
 *   - Image attach (Paperclip) is preserved because classic implements it.
 *   - Errors are surfaced as an AI error bubble, mirroring classic.
 *
 * Only the JSX/styling changes — V2 tokens, dark dense bubbles, V2 primitives.
 * Citations: the classic data model carries no `sources` array, so the citation
 * chip is rendered defensively from an optional `source`/`cite` pair using
 * SOURCE_META colors (matches the design_reference Chat stub), and a
 * "Scope generated" accent pill is shown when the response produced scope_data.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { cn, getProjectDisplayName } from '../../lib/utils';
import { BackendAPI } from '../../lib/api';
import useProjectStore from '../../stores/projectStore';
import useAuthStore from '../../stores/authStore';
import { ChatMessage, Project } from '../../types';

import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Pill } from '../components/ui/pill';
import { EmptyState } from '../components/ui/empty-state';
import { Avatar } from '../components/ui/avatar';
import { Logo } from '../components/Logo';
import {
  IconChat,
  IconMic,
  IconChevr,
  IconX,
  IconUpload,
} from '../components/icons';
import { sourceMeta } from '../lib/semantics';

// ----------------------------------------------------------------- //
//  Local message shape — classic ChatMessage, plus optional citation //
//  fields the design reference renders (source + cite). These are    //
//  additive and never required, so classic behaviour is unchanged.   //
// ----------------------------------------------------------------- //

/** A chat message extended with the design-reference citation fields. */
type V2ChatMessage = ChatMessage & {
  /** Source key understood by SOURCE_META (e.g. 'code_requirement'). */
  source?: string;
  /** Citation string (e.g. an FBC reference). */
  cite?: string;
};

/** Suggested prompts for the empty state (presentation only). */
const SUGGESTED_PROMPTS = [
  'Explain why ice & water barrier is required in HVHZ.',
  'Draft demolition line items for a water-damaged bathroom.',
  'Check this scope for missing companion items.',
];

/**
 * V2 AI Chat page. Reuses the classic chat send flow and state machine
 * verbatim; only the presentation is re-skinned to the V2 design system.
 *
 * @returns The V2 chat screen, mounted inside the V2 shell content area.
 */
export default function V2ChatPage() {
  const { projectId } = useParams<{ projectId?: string }>();
  const { getProjectById, selectedProject } = useProjectStore();
  const user = useAuthStore((s) => s.user);

  const [project, setProject] = useState<Project | null>(null);
  const [messages, setMessages] = useState<V2ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // --- Effects (copied from classic) ---

  // Fetch project details when projectId changes; fall back to the sticky
  // selectedProject from the store so /chat (no param) still has context.
  useEffect(() => {
    const fetchProjectData = async () => {
      if (projectId) {
        const fetchedProject = await getProjectById(projectId);
        if (fetchedProject) {
          setProject(fetchedProject);
        }
      } else {
        setProject(null);
      }
    };
    fetchProjectData();
  }, [projectId, getProjectById]);

  // Scroll to the bottom on new messages / loading indicator.
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // The active project context: explicit param project, else sticky selection.
  const contextProject = project ?? (projectId ? null : selectedProject);

  // --- Event handlers (send flow copied verbatim from classic) ---

  const handleSendMessage = async () => {
    if ((!input.trim() && !image) || isLoading) return;

    const userMessage: V2ChatMessage = {
      id: Date.now().toString(),
      sender: 'user',
      text: input,
      image_url: imagePreview || undefined,
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setImage(null);
    setImagePreview(null);
    setIsLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('question', input);
      if (image) {
        formData.append('image', image);
      }
      // Reason: project context is passed exactly as classic — project_id from
      // the route param (when present) plus project_address for location.
      if (projectId) {
        formData.append('project_id', projectId);
      }
      if (contextProject?.address) {
        formData.append('project_address', contextProject.address);
      }

      const response = await BackendAPI.sendMessageToChatbot(formData);

      const aiMessage: V2ChatMessage = {
        id: (Date.now() + 1).toString(),
        sender: 'ai',
        text: response.answer,
        scope_data: response.scope_data,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, aiMessage]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setError(errorMessage);
      const errorMsg: V2ChatMessage = {
        id: (Date.now() + 1).toString(),
        sender: 'ai',
        text: `Sorry, I encountered an error: ${errorMessage}`,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Enter to send, Shift+Enter for newline — matches classic.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Auto-grow the textarea (presentation refinement; capped so the input bar
  // never eats the message list).
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    }
  };

  const userName = user?.displayName || user?.email || 'You';
  const canSend = (input.trim().length > 0 || image !== null) && !isLoading;

  // --- Render ---

  return (
    <div className="flex h-full min-h-0 flex-col bg-[var(--bg-1)]">
      {/* Header */}
      <div className="flex flex-none items-center gap-3 border-b border-[var(--bd-subtle)] px-5 py-3">
        <span
          className="grid h-7 w-7 place-items-center rounded-[7px] border border-[var(--bd)]"
          style={{
            background:
              'radial-gradient(120% 120% at 30% 20%, color-mix(in oklab, var(--accent) 30%, var(--bg-3)), var(--bg-3))',
            boxShadow: 'inset 0 1px 0 var(--hi-top)',
          }}
        >
          <Logo size={15} />
        </span>
        <div className="min-w-0">
          <div className="text-[13px] font-[600] text-[var(--tx-hi)]">AI Assistant</div>
          <div className="text-[11.5px] text-[var(--tx-lo)]">
            Codes, line items, and scope checks
          </div>
        </div>
        {contextProject && (
          <Pill kind="accent" className="ml-auto">
            Project: {getProjectDisplayName(contextProject)}
          </Pill>
        )}
      </div>

      {/* Message list */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-[820px] flex-col gap-4 px-[22px] py-6">
          {messages.length === 0 && !isLoading && (
            <EmptyState
              icon={<IconChat />}
              title="Ask me anything about this project"
              description="I can draft line items, explain a code choice, or check a scope for completeness."
              action={
                <div className="flex max-w-[560px] flex-wrap justify-center gap-2">
                  {SUGGESTED_PROMPTS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setInput(p)}
                      className={cn(
                        'rounded-[var(--r-md)] border border-[var(--bd-subtle)] bg-[var(--bg-2)] px-3 py-2',
                        'text-left text-[12px] text-[var(--tx-mid)]',
                        'transition-colors hover:border-[var(--accent-line)] hover:text-[var(--tx-hi)]',
                      )}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              }
            />
          )}

          {messages.map((msg) => {
            const isUser = msg.sender === 'user';
            const meta = msg.source ? sourceMeta(msg.source) : null;
            return (
              <div
                key={msg.id}
                className={cn('flex gap-[10px]', isUser ? 'flex-row-reverse' : 'flex-row')}
              >
                {/* Avatar */}
                {isUser ? (
                  <Avatar name={userName} size={28} className="mt-0.5" />
                ) : (
                  <span
                    className="mt-0.5 grid h-7 w-7 flex-none place-items-center rounded-[7px] border border-[var(--bd)]"
                    style={{
                      background:
                        'radial-gradient(120% 120% at 30% 20%, color-mix(in oklab, var(--accent) 30%, var(--bg-3)), var(--bg-3))',
                      boxShadow: 'inset 0 1px 0 var(--hi-top)',
                    }}
                  >
                    <Logo size={15} />
                  </span>
                )}

                {/* Bubble + citation */}
                <div className="max-w-[74%]">
                  <div
                    className={cn(
                      'rounded-[var(--r-md)] border px-[14px] py-[11px] text-[13px] leading-[1.55] text-[var(--tx-hi)]',
                      isUser
                        ? 'border-[var(--accent-line)] bg-[var(--accent-soft)]'
                        : 'border-[var(--bd-subtle)] bg-[var(--bg-2)]',
                    )}
                  >
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
                        h3: ({ node, ...props }) => (
                          <h3 className="mb-2 mt-4 text-[13px] font-semibold" {...props} />
                        ),
                        ul: ({ node, ...props }) => (
                          <ul className="mb-2 list-disc pl-5" {...props} />
                        ),
                        ol: ({ node, ...props }) => (
                          <ol className="mb-2 list-decimal pl-5" {...props} />
                        ),
                        a: ({ node, ...props }) => (
                          <a
                            className="text-[var(--accent-ink)] underline underline-offset-2"
                            {...props}
                          />
                        ),
                        code: ({ node, ...props }) => (
                          <code
                            className="rounded bg-[var(--bg-inset)] px-1 font-mono text-[12px] text-[var(--tx-hi)]"
                            {...props}
                          />
                        ),
                      }}
                    >
                      {msg.text}
                    </ReactMarkdown>
                    {msg.image_url && (
                      <img
                        src={msg.image_url}
                        alt="Attachment"
                        className="mt-2 max-w-[220px] rounded-[var(--r-md)] border border-[var(--bd-subtle)]"
                      />
                    )}
                  </div>

                  {/* Source citation chip (design_reference .src style) */}
                  {meta && (
                    <div className="mt-[7px]">
                      <span className="inline-flex items-center gap-[6px] text-[11px] text-[var(--tx-lo)]">
                        <span
                          className="h-[7px] w-[7px] flex-none rounded-full"
                          style={{
                            background: meta.color,
                            boxShadow: `0 0 6px color-mix(in oklab, ${meta.color} 60%, transparent)`,
                          }}
                          aria-hidden="true"
                        />
                        {meta.label}
                        {msg.cite && (
                          <>
                            {' · '}
                            <span className="font-mono">{msg.cite}</span>
                          </>
                        )}
                      </span>
                    </div>
                  )}

                  {/* Scope-generated indicator (classic only exposes scope_data) */}
                  {!isUser && msg.scope_data && (
                    <div className="mt-[7px]">
                      <Pill kind="accent">Scope generated</Pill>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Typing indicator */}
          {isLoading && (
            <div className="flex gap-[10px]">
              <span
                className="mt-0.5 grid h-7 w-7 flex-none place-items-center rounded-[7px] border border-[var(--bd)]"
                style={{
                  background:
                    'radial-gradient(120% 120% at 30% 20%, color-mix(in oklab, var(--accent) 30%, var(--bg-3)), var(--bg-3))',
                  boxShadow: 'inset 0 1px 0 var(--hi-top)',
                }}
              >
                <Logo size={15} />
              </span>
              <div className="flex items-center gap-1 rounded-[var(--r-md)] border border-[var(--bd-subtle)] bg-[var(--bg-2)] px-[14px] py-[13px]">
                <span className="h-[6px] w-[6px] animate-bounce rounded-full bg-[var(--tx-faint)] [animation-delay:-0.3s]" />
                <span className="h-[6px] w-[6px] animate-bounce rounded-full bg-[var(--tx-faint)] [animation-delay:-0.15s]" />
                <span className="h-[6px] w-[6px] animate-bounce rounded-full bg-[var(--tx-faint)]" />
              </div>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>
      </div>

      {/* Input bar */}
      <div className="flex-none border-t border-[var(--bd-subtle)] px-[18px] py-[14px]">
        <div className="mx-auto w-full max-w-[820px]">
          {error && (
            <div className="mb-2 text-[11.5px] text-[var(--bad)]">{error}</div>
          )}
          {imagePreview && (
            <div className="relative mb-2 inline-block">
              <img
                src={imagePreview}
                alt="Preview"
                className="h-16 w-16 rounded-[var(--r-md)] border border-[var(--bd-subtle)] object-cover"
              />
              <button
                type="button"
                onClick={() => {
                  setImage(null);
                  setImagePreview(null);
                }}
                className="absolute -right-2 -top-2 grid h-5 w-5 place-items-center rounded-full bg-[var(--bg-4)] text-[var(--tx-hi)] [&_svg]:h-3 [&_svg]:w-3"
                aria-label="Remove image"
              >
                <IconX />
              </button>
            </div>
          )}
          <div className="flex items-end gap-2 rounded-[var(--r-md)] border border-[var(--bd)] bg-[var(--bg-2)] p-[10px] shadow-[inset_0_1px_1px_rgba(0,0,0,.15)] focus-within:border-[var(--accent-line)]">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Ask about a code, draft items, or check a scope…"
              rows={1}
              disabled={isLoading}
              className="min-h-[24px] flex-1 resize-none border-0 bg-transparent p-0 shadow-none focus:shadow-none"
            />
            <Button
              variant="ghost"
              size="sm"
              className="px-2"
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading}
              aria-label="Attach image"
            >
              <IconUpload />
            </Button>
            {/* Mic hint — classic does not implement dictation; decorative. */}
            <Button
              variant="ghost"
              size="sm"
              className="px-2"
              disabled
              aria-label="Voice input (coming soon)"
              title="Voice input coming soon"
            >
              <IconMic />
            </Button>
            <Button
              variant="primary"
              size="md"
              className="w-8 justify-center px-0"
              onClick={handleSendMessage}
              disabled={!canSend}
              aria-label="Send message"
            >
              <IconChevr />
            </Button>
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageSelect}
            className="hidden"
            accept="image/*"
          />
        </div>
      </div>
    </div>
  );
}
